import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Tag } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import { blogPosts } from '../data/mockData';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.id === parseInt(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#3B3838' }}>
            Artículo no encontrado
          </h2>
          <Button onClick={() => navigate('/')}>
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  return (
    <div>
      <Header />
      
      {/* Hero Image Section */}
      <section className="relative pt-20">
        <div className="relative h-[500px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
            }}
          />
          
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl">
                {/* Category Badge */}
                <div 
                  className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4"
                  style={{ 
                    backgroundColor: '#FFC000',
                    color: '#3B3838'
                  }}
                >
                  {post.category}
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  {post.title}
                </h1>
                
                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-6 text-white/90">
                  <div className="flex items-center gap-2">
                    <Calendar size={18} />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={18} />
                    <span>{post.readTime} de lectura</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Back Button & Share */}
            <div className="flex items-center justify-between mb-8">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 font-semibold transition-colors"
                style={{ color: '#2E75B6' }}
              >
                <ArrowLeft size={20} />
                Volver al Blog
              </button>
              
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all hover:scale-105"
                style={{ 
                  backgroundColor: '#E8F2FA',
                  color: '#2E75B6'
                }}
              >
                <Share2 size={18} />
                Compartir
              </button>
            </div>

            {/* Article Content */}
            <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              {/* Excerpt/Summary */}
              <div 
                className="text-xl leading-relaxed mb-8 pb-8 border-b-2"
                style={{ 
                  color: '#3B3838',
                  borderColor: '#E5E7EB'
                }}
              >
                <strong>Resumen:</strong> {post.excerpt}
              </div>

              {/* Main Content */}
              <div 
                className="prose prose-lg max-w-none"
                style={{ color: '#3B3838' }}
              >
                {post.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-6 text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Article Footer */}
              <div 
                className="mt-12 pt-8 border-t-2"
                style={{ borderColor: '#E5E7EB' }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <Tag size={20} style={{ color: '#2E75B6' }} />
                  <span className="font-semibold" style={{ color: '#3B3838' }}>
                    Categoría:
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ 
                      backgroundColor: '#E8F2FA',
                      color: '#2E75B6'
                    }}
                  >
                    {post.category}
                  </span>
                </div>
              </div>
            </article>

            {/* CTA Section */}
            <div 
              className="mt-12 p-10 rounded-2xl text-center"
              style={{ 
                background: 'linear-gradient(135deg, #2E75B6 0%, #7030A0 100%)'
              }}
            >
              <h3 className="text-3xl font-bold text-white mb-4">
                ¿Listo para transformar sus espacios?
              </h3>
              <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
                Descubra cómo nuestros servicios profesionales de limpieza pueden 
                mejorar su ambiente laboral y fortalecer su imagen corporativa.
              </p>
              <Button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                size="lg"
                className="font-semibold text-lg"
                style={{ 
                  backgroundColor: '#FFC000',
                  color: '#3B3838'
                }}
              >
                Solicitar Cotización Gratuita
              </Button>
            </div>

            {/* Related Articles */}
            <div className="mt-16">
              <h3 
                className="text-2xl font-bold mb-8"
                style={{ color: '#3B3838' }}
              >
                Más Artículos
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {blogPosts
                  .filter(p => p.id !== post.id)
                  .slice(0, 2)
                  .map((relatedPost) => (
                    <div
                      key={relatedPost.id}
                      onClick={() => navigate(`/blog/${relatedPost.id}`)}
                      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                    >
                      <div className="h-48 overflow-hidden">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <div className="p-6">
                        <div 
                          className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
                          style={{ 
                            backgroundColor: '#FFF3CD',
                            color: '#FFC000'
                          }}
                        >
                          {relatedPost.category}
                        </div>
                        <h4 
                          className="text-lg font-bold mb-2 group-hover:text-[#2E75B6] transition-colors"
                          style={{ color: '#3B3838' }}
                        >
                          {relatedPost.title}
                        </h4>
                        <p 
                          className="text-sm"
                          style={{ color: '#6B7280' }}
                        >
                          {relatedPost.excerpt.substring(0, 120)}...
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
